"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.derivedDebounceFn = exports.debounceFn = exports.controlledDebounceFn = void 0;
const getControlledPromise_1 = require("./getControlledPromise");
function controlledDebounceFn(fn) {
    // keep track of the last promise controller
    // so we can resolve it with { debounce: true }
    let lastPromiseController;
    let timerId;
    let interval;
    function debounce(...args) {
        // clear
        clearTimeout(timerId);
        lastPromiseController === null || lastPromiseController === void 0 ? void 0 : lastPromiseController.resolve({ debounced: true });
        // recreate
        // we need a local copy of the controller, because
        // otherwise the callback inside may use a different reference
        // leading to very strange behavior
        const localPromiseController = (0, getControlledPromise_1.getControlledPromise)();
        timerId = window.setTimeout(async () => {
            try {
                const data = await fn(...args);
                localPromiseController.resolve({ data });
            }
            catch (e) {
                localPromiseController.reject(e);
            }
            timerId = undefined;
        }, interval);
        lastPromiseController = localPromiseController;
        return localPromiseController.promise;
    }
    function setDebounceInterval(n) {
        interval = n;
    }
    return [debounce, setDebounceInterval];
}
exports.controlledDebounceFn = controlledDebounceFn;
/**
 * Creates a debounced function that awaits the specified interval before being executed.
 *
 * Example:
 *
 * const debouncedFn = debounceFn(fn, 500)
 *
 * debouncedFn()
 * debouncedFn() // runs once after 500ms
 */
function debounceFn(fn, interval) {
    const [debounce, setDebounceInterval] = controlledDebounceFn(fn);
    setDebounceInterval(interval);
    return debounce;
}
exports.debounceFn = debounceFn;
/**
 * Creates a debounced function with a dynamic interval.
 * The interval is derived from the args passed onto the debounced function.
 *
 * Example:
 *
 * const debouncedFn = derivedDebounceFn(fn, (searchTerm) => {
 *    searchTerm.length < 3 ? 1000 : 300)
 * })
 *
 * debouncedFn('Ca') // debounces with 1000
 * debouncedFn('Cat') // debounces with 300
 */
function derivedDebounceFn(fn, getInterval) {
    const [debounce, setDebounceInterval] = controlledDebounceFn(fn);
    return (...args) => {
        setDebounceInterval(getInterval(...args));
        return debounce(...args);
    };
}
exports.derivedDebounceFn = derivedDebounceFn;
